import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { APIResponse } from '../../../utils'
import { Contact } from '../types/contact'

interface UseCustomerSubmissionHandlersProps {
  onSubmit: (customer: Contact) => void
  shouldInvalidateAddress?: boolean
  successMessage: string
}

export const useCustomerSubmissionHandlers = ({
  onSubmit,
  shouldInvalidateAddress,
  successMessage,
}: UseCustomerSubmissionHandlersProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const handleCustomerSubmitSuccess = useCallback(
    async (data: APIResponse<Contact>) => {
      const contact = data.contacts[0]

      await queryClient.invalidateQueries([QueryKeys.Contact, contact.id])
      await queryClient.invalidateQueries(QueryKeys.Contacts)

      if (shouldInvalidateAddress && contact.shippingAddressId) {
        await queryClient.invalidateQueries([QueryKeys.Addresses, contact.shippingAddressId])
      }

      notify({
        id: NotificationKeys.CreateContact,
        variant: 'success',
        message: successMessage,
      })

      onSubmit(contact)
    },
    [shouldInvalidateAddress, onSubmit, queryClient, successMessage],
  )

  const handleCustomerSubmitError = useCallback(() => {
    notify({
      id: NotificationKeys.CreateContact,
      message: t('customer_modal.form.edit.notification.error'),
      variant: 'error',
    })
  }, [t])

  return {
    handleCustomerSubmitError,
    handleCustomerSubmitSuccess,
  }
}
